import { SourceDealActions } from "@/components/operations/SourceDealActions";
import { WorkflowSummaryCard } from "@/components/operations/WorkflowSummaryCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  useAllClients,
  useBookingsBySoId,
  useCreateClient,
  useInventoryById,
  useInvoicesBySoId,
  useSalesOrderById,
  useUpdateSalesOrder,
  useUser,
  useUsers,
} from "@/hooks";
import { formatDateOnly, formatTimestamp } from "@/utils/formatting";
import {
  getClientSearchKeywords,
  getClientSelectLabel,
  removeUndefinedValues,
} from "@/utils";
import { getSalesOrderTotal } from "@/utils/order-total";
import { DollarSign, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ContactDialog } from "@/components/contacts/ContactDialog";
import { ContactFormValues } from "@/hooks/forms/entities/use-contact-form";
import { ClientDataSchema } from "@/core";
import { useUserStore } from "@/stores/user";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { SpecialAttributeMultiSelect } from "@/components/special-attributes";
import { SalesOrderRateFields } from "./SalesOrderRateFields";
import { useProductsStore } from "@/stores/products";
import {
  buildInventoryWorkspacePath,
  formatCurrency,
  formatNumber,
  formatShortId,
  formatStatusLabel,
  formatUserDisplayName,
  getSalesOrderAttentionDetails,
  getSalesOrderErrorMessage,
  resolveProductCode,
  SALES_ORDER_DRAFT_NOTICE,
} from "./operations-shared";

interface SalesOrderSheetProps {
  soId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SalesOrderSheet(props: Readonly<SalesOrderSheetProps>) {
  const { soId, open, onOpenChange } = props;
  const salesOrderQuery = useSalesOrderById(soId);
  const updateSalesOrderMutation = useUpdateSalesOrder();
  const clientsQuery = useAllClients();
  const createClientMutation = useCreateClient();
  const { hasClientVendorWriteAccess, isAdmin } = useUserStore();
  const canApprove = isAdmin();
  const invoicesQuery = useInvoicesBySoId(soId);
  const bookingsQuery = useBookingsBySoId(soId);
  const salesOrder = salesOrderQuery.data;
  const inventoryQuery = useInventoryById(salesOrder?.inventoryId || "");
  const soldByUserQuery = useUser(salesOrder?.soldByUserId || "");
  const usersQuery = useUsers();
  const invoices = invoicesQuery.data || [];
  const bookings = bookingsQuery.data || [];
  const health = salesOrder
    ? getSalesOrderAttentionDetails({
        status: salesOrder.status,
        invoiceCount: invoices.length,
      })
    : undefined;
  const totalAmount = salesOrder
    ? getSalesOrderTotal({
        revenue: salesOrder.revenue,
        taxCost: salesOrder.taxCost,
        totalAmount: salesOrder.totalAmount,
      })
    : 0;

  const [status, setStatus] = useState<
    | "draft"
    | "allocated"
    | "open_booking"
    | "ticketed"
    | "delivered"
    | "cancelled"
  >("draft");
  const [flightDate, setFlightDate] = useState("");
  const [bookingEndDate, setBookingEndDate] = useState("");
  const [bookingTbd, setBookingTbd] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState("");
  const [totalMilesSold, setTotalMilesSold] = useState("");
  const [revenue, setRevenue] = useState("");
  const [taxCost, setTaxCost] = useState("");
  const [salesChannel, setSalesChannel] = useState<
    "hubspot" | "manual" | "agent" | "other"
  >("manual");
  const [notes, setNotes] = useState("");
  const [specialAttributeIds, setSpecialAttributeIds] = useState<string[]>([]);
  const [transferProductId, setTransferProductId] = useState("");
  const [clientId, setClientId] = useState("");
  const [soldByUserId, setSoldByUserId] = useState("");
  const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);
  const [saveError, setSaveError] = useState("");
  const clients = useMemo(
    () => clientsQuery.data?.pages.flat() ?? [],
    [clientsQuery.data],
  );
  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: getClientSelectLabel({ client }),
        keywords: getClientSearchKeywords({ client }),
      })),
    [clients],
  );
  const soldByOptions = useMemo(
    () =>
      (usersQuery.data ?? []).map((user) => ({
        value: user.id,
        label: formatUserDisplayName(user.email),
        keywords: [formatUserDisplayName(user.email), user.email, user.id],
      })),
    [usersQuery.data],
  );
  const products = useProductsStore((state) => state.products);
  const getProductName = useProductsStore((state) => state.getProductName);
  const getProductById = useProductsStore((state) => state.getProductById);
  const transferProductOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: product.name,
        keywords: [product.name, product.id],
      })),
    [products],
  );

  useEffect(() => {
    if (!open || !salesOrder) {
      return;
    }

    setStatus(salesOrder.status);
    setFlightDate(formatDateOnly(salesOrder.flightDate) || "");
    setBookingEndDate(formatDateOnly(salesOrder.bookingEndDate) || "");
    setBookingTbd(salesOrder.bookingTbd === true);
    setPurchaseDate(formatDateOnly(salesOrder.purchaseDate ?? salesOrder.createdAt) || "");
    setTotalMilesSold(String(salesOrder.totalMilesSold));
    setRevenue(String(salesOrder.revenue));
    setTaxCost(String(salesOrder.taxCost ?? 0));
    setSalesChannel(salesOrder.salesChannel);
    setNotes(salesOrder.notes || "");
    setSpecialAttributeIds(salesOrder.specialAttributeIds ?? []);
    setTransferProductId(salesOrder.transferProductId || "");
    setClientId(salesOrder.clientId || "");
    setSoldByUserId(salesOrder.soldByUserId || "");
    setSaveError("");
  }, [open, salesOrder?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!salesOrder) {
      return;
    }

    setSaveError("");

    // Miles sold moves stock in and out of the linked inventory, so a blank or
    // negative entry would corrupt the inventory balance rather than just this
    // order.
    const totalMilesSoldValue = Number(totalMilesSold);

    if (!Number.isFinite(totalMilesSoldValue) || totalMilesSoldValue <= 0) {
      setSaveError("Miles sold must be a number greater than zero.");
      return;
    }

    try {
      await updateSalesOrderMutation.mutateAsync({
        soId: salesOrder.id,
        data: {
          status,
          flightDate: bookingTbd ? undefined : flightDate || undefined,
          bookingEndDate: bookingTbd ? undefined : bookingEndDate || undefined,
          bookingTbd,
          purchaseDate: purchaseDate || undefined,
          totalMilesSold: totalMilesSoldValue,
          revenue: Number(revenue),
          taxCost: Number(taxCost),
          salesChannel,
          notes: notes.trim() || undefined,
          specialAttributeIds,
          transferProductId: transferProductId || undefined,
          clientId: clientId || undefined,
          soldByUserId: soldByUserId || undefined,
        },
      });
    } catch (error: unknown) {
      // Raising the miles sold — or approving / re-opening an order — takes
      // miles out of stock, which fails when they were sold elsewhere first.
      setSaveError(getSalesOrderErrorMessage(error));
    }
  };

  const handleApprove = async () => {
    if (!salesOrder) {
      return;
    }

    setSaveError("");

    try {
      await updateSalesOrderMutation.mutateAsync({
        soId: salesOrder.id,
        data: { status: "allocated" },
      });
      setStatus("allocated");
    } catch (error: unknown) {
      setSaveError(getSalesOrderErrorMessage(error));
    }
  };

  const handleCreateClient = async (data: ContactFormValues) => {
    const normalizedData = {
      ...data,
      secondaryPhone: data.secondaryPhone?.trim() || undefined,
      secondaryEmail: data.secondaryEmail?.trim() || undefined,
      companyName: data.companyName?.trim() || undefined,
      type: data.type || undefined,
    };
    const parsedData = ClientDataSchema.parse(normalizedData);
    const cleanData = removeUndefinedValues(parsedData);
    const newClientId = await createClientMutation.mutateAsync({
      data: cleanData,
    });

    setClientId(newClientId);
    setIsCreateClientOpen(false);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-full overflow-y-auto p-0 md:max-w-2xl"
      >
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>Sell Order Details</SheetTitle>
            <SheetDescription>
              Review the linked inventory and receivables state.
            </SheetDescription>
          </SheetHeader>
        </div>

        {!salesOrder ? (
          <div className="px-6 py-6 text-sm text-muted-foreground">
            {salesOrderQuery.isLoading ? "Loading..." : "Sell order not found."}
          </div>
        ) : (
          <div className="space-y-6 px-6 py-6 text-sm">
            <WorkflowSummaryCard
              title={salesOrder.productName}
              description={
                salesOrder.clientDisplayName ||
                salesOrder.clientEmail ||
                "Unknown client"
              }
              status={salesOrder.status}
              health={health?.state}
              healthReason={health?.reason}
              metrics={[
                {
                  label: "Miles Sold",
                  value: `${formatNumber({ value: salesOrder.totalMilesSold })} miles`,
                },
                {
                  label: "Miles Amount",
                  value: formatCurrency({ value: salesOrder.revenue }),
                },
                {
                  label: "Tax",
                  value: formatCurrency({ value: salesOrder.taxCost }),
                },
                {
                  label: "Total",
                  value: formatCurrency({ value: totalAmount }),
                },
                { label: "Invoices", value: String(invoices.length) },
                {
                  label: "Inventory",
                  value:
                    inventoryQuery.data?.name ||
                    salesOrder.inventoryId.slice(0, 8),
                },
              ]}
              footer={
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm">
                    <RouterLink
                      to={
                        invoices.length > 0
                          ? `/operations/finance?tab=receivables&soId=${salesOrder.id}`
                          : `/operations/finance?tab=receivables&soId=${salesOrder.id}&amountDue=${totalAmount}`
                      }
                    >
                      <DollarSign className="h-4 w-4" />
                      {invoices.length > 0 ? "Open Finance" : "Create Invoice"}
                    </RouterLink>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <RouterLink
                      to={buildInventoryWorkspacePath({
                        inventoryId: salesOrder.inventoryId,
                        salesOrderId: salesOrder.id,
                        focus: "sell-orders",
                      })}
                      onClick={() => onOpenChange(false)}
                    >
                      Open Inventory Workspace
                    </RouterLink>
                  </Button>
                  <SourceDealActions sourceDealId={salesOrder.sourceDealId} />
                </div>
              }
            />

            {salesOrder.status === "draft" ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sky-300 bg-sky-50 p-4 text-sky-900">
                <div>{SALES_ORDER_DRAFT_NOTICE}</div>
                {canApprove ? (
                  <Button
                    size="sm"
                    onClick={handleApprove}
                    loading={updateSalesOrderMutation.isPending}
                    loadingText="Approving..."
                  >
                    Approve
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="rounded-xl border p-4 space-y-3">
              <div className="flex items-baseline gap-2">
                <div className="font-medium">Order Details</div>
                <span
                  className="font-mono text-xs text-muted-foreground"
                  title={salesOrder.id}
                >
                  {formatShortId(salesOrder.id)}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge>{formatStatusLabel(salesOrder.status)}</Badge>
                <Badge variant="outline">
                  {formatStatusLabel(salesOrder.salesChannel)}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Product code</span>{" "}
                  {resolveProductCode({
                    productCode: getProductById(salesOrder.productId)
                      ?.productCode,
                    productName: salesOrder.productName,
                  })}
                </div>
                <div>
                  <span className="text-muted-foreground">Created</span>{" "}
                  {formatTimestamp(salesOrder.createdAt, "yyyy-MM-dd HH:mm") ||
                    "-"}
                </div>
                <div>
                  <span className="text-muted-foreground">Order date</span>{" "}
                  {formatDateOnly(salesOrder.purchaseDate ?? salesOrder.createdAt) || "-"}
                </div>
                <div>
                  <span className="text-muted-foreground">Flight date</span>{" "}
                  {formatDateOnly(salesOrder.flightDate) || "-"}
                </div>
                <div>
                  <span className="text-muted-foreground">Sold by</span>{" "}
                  {soldByUserQuery.data?.email ||
                    salesOrder.soldByUserEmail ||
                    "-"}
                </div>
                <div>
                  <span className="text-muted-foreground">Booking ends</span>{" "}
                  {formatDateOnly(salesOrder.bookingEndDate) || "-"}
                </div>
                <div>
                  <span className="text-muted-foreground">Tax cost</span>{" "}
                  {formatCurrency({ value: salesOrder.taxCost })}
                </div>
                <div>
                  <span className="text-muted-foreground">Total amount</span>{" "}
                  {formatCurrency({ value: totalAmount })}
                </div>
                <div>
                  <span className="text-muted-foreground">CPM</span>{" "}
                  {salesOrder.totalMilesSold > 0
                    ? `${((salesOrder.revenue / salesOrder.totalMilesSold) * 100).toFixed(2)}¢/mi`
                    : "-"}
                </div>
                <div>
                  <span className="text-muted-foreground">Transfer product</span>{" "}
                  {salesOrder.transferProductId
                    ? getProductName(salesOrder.transferProductId)
                    : "-"}
                </div>
              </div>
              {salesOrder.sourceDealId ? (
                <div className="space-y-2">
                  <div className="text-muted-foreground">Source deal</div>
                  <SourceDealActions sourceDealId={salesOrder.sourceDealId} />
                </div>
              ) : null}
            </div>

            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Edit Sell Order</div>
              <div className="space-y-1">
                <Label>Client (Sold To)</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <SearchableSelect
                      value={clientId}
                      onChange={setClientId}
                      placeholder="Select client"
                      searchPlaceholder="Search client..."
                      emptyText="No clients found."
                      options={clientOptions}
                    />
                  </div>
                  {hasClientVendorWriteAccess() ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsCreateClientOpen(true)}
                    >
                      <Plus className="h-4 w-4" />
                      New
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="space-y-1">
                <Label>Sold By</Label>
                <SearchableSelect
                  value={soldByUserId}
                  onChange={setSoldByUserId}
                  placeholder="Select sold-by name"
                  searchPlaceholder="Search sold-by name..."
                  emptyText="No users found."
                  options={soldByOptions}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) => setStatus(value as typeof status)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="allocated">
                        Allocated
                      </SelectItem>
                      <SelectItem value="open_booking">
                        Open Booking
                      </SelectItem>
                      <SelectItem value="ticketed">
                        Ticketed
                      </SelectItem>
                      <SelectItem value="delivered">
                        Delivered
                      </SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Sales Channel</Label>
                  <Select
                    value={salesChannel}
                    onValueChange={(value) =>
                      setSalesChannel(value as typeof salesChannel)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="hubspot">HubSpot</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={bookingTbd}
                  onCheckedChange={(checked) => {
                    const next = checked === true;
                    setBookingTbd(next);
                    if (next) {
                      setFlightDate("");
                      setBookingEndDate("");
                    }
                  }}
                />
                <span>TBD — flight not yet scheduled (keeps inventory blocked)</span>
              </label>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label>Order Date</Label>
                  <Input
                    type="date"
                    value={purchaseDate}
                    onChange={(event) => setPurchaseDate(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Flight Date</Label>
                  <Input
                    type="date"
                    value={flightDate}
                    onChange={(event) => setFlightDate(event.target.value)}
                    disabled={bookingTbd}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Booking End</Label>
                  <Input
                    type="date"
                    value={bookingEndDate}
                    onChange={(event) => setBookingEndDate(event.target.value)}
                    disabled={bookingTbd}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Miles Sold</Label>
                  <Input
                    value={totalMilesSold}
                    onChange={(event) => setTotalMilesSold(event.target.value)}
                    placeholder="30000"
                  />
                  {inventoryQuery.data ? (
                    <div className="text-xs text-muted-foreground">
                      {formatNumber({
                        value: inventoryQuery.data.availableMiles,
                      })}{" "}
                      more available in inventory
                    </div>
                  ) : null}
                </div>
                <SalesOrderRateFields
                  milesSold={totalMilesSold}
                  revenue={revenue}
                  onRevenueChange={setRevenue}
                />
                <div className="space-y-1">
                  <Label>Tax Cost</Label>
                  <Input
                    value={taxCost}
                    onChange={(event) => setTaxCost(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Special Attributes</Label>
                  <SpecialAttributeMultiSelect
                    value={specialAttributeIds}
                    onChange={setSpecialAttributeIds}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Transfer Product</Label>
                  <SearchableSelect
                    value={transferProductId}
                    onChange={setTransferProductId}
                    placeholder="Select transfer product"
                    searchPlaceholder="Search transfer product..."
                    emptyText="No product found."
                    options={transferProductOptions}
                    clearable
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Operational notes"
                />
              </div>
              {saveError ? (
                <div className="text-sm text-destructive">{saveError}</div>
              ) : null}
              <div className="flex justify-end">
                <Button
                  onClick={handleSave}
                  loading={updateSalesOrderMutation.isPending}
                  loadingText="Saving..."
                >
                  Save Sell Order
                </Button>
              </div>
            </div>

            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Inventory Credentials</div>
              {inventoryQuery.data ? (
                <>
                  <div>Name: {inventoryQuery.data.name}</div>
                  <div>
                    Owner: {inventoryQuery.data.accountOwnerName || "-"}
                  </div>
                  <div>Email: {inventoryQuery.data.accountEmail || "-"}</div>
                  <div>
                    Status:{" "}
                    {formatStatusLabel(inventoryQuery.data.accountStatus)}
                  </div>
                  <div>
                    2FA: {formatStatusLabel(inventoryQuery.data.twoFaStatus)}
                  </div>
                  <div>
                    Available Miles:{" "}
                    {formatNumber({
                      value: inventoryQuery.data.availableMiles,
                    })}
                  </div>
                </>
              ) : (
                <div className="text-muted-foreground">
                  {inventoryQuery.isLoading
                    ? "Loading..."
                    : "Inventory not found."}
                </div>
              )}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border p-4 space-y-3">
                <div className="font-medium">Invoices ({invoices.length})</div>
                {invoices.length === 0 ? (
                  <div className="text-muted-foreground">No invoices yet.</div>
                ) : (
                  invoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between rounded-lg border bg-muted/20 p-3"
                    >
                      <div>
                        <div className="font-medium">
                          {formatStatusLabel(invoice.status)}
                        </div>
                        <div className="text-muted-foreground">
                          Due{" "}
                          {formatTimestamp(invoice.dueAt, "yyyy-MM-dd") || "-"}
                        </div>
                      </div>
                      <div>{formatCurrency({ value: invoice.amountDue })}</div>
                    </div>
                  ))
                )}
              </div>

              <div className="rounded-xl border p-4 space-y-3">
                <div className="font-medium">Bookings ({bookings.length})</div>
                {bookings.length === 0 ? (
                  <div className="text-muted-foreground">No bookings yet.</div>
                ) : (
                  bookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="flex items-center justify-between rounded-lg border bg-muted/20 p-3"
                    >
                      <span>{formatStatusLabel(booking.status)}</span>
                      <span>
                        {formatTimestamp(
                          booking.updatedAt,
                          "yyyy-MM-dd HH:mm",
                        ) || "-"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
    <ContactDialog
      open={isCreateClientOpen}
      onOpenChange={setIsCreateClientOpen}
      mode="create"
      entityLabel="client"
      title="Create client"
      description="Add a new client and assign them to this sell order."
      submitText="Create client"
      schema={ClientDataSchema}
      variant="client"
      isSubmitting={createClientMutation.isPending}
      onSubmit={handleCreateClient}
    />
    </>
  );
}
